import generateGrayIcon from '@/assets/images/play/generate-gray.svg';
import generateIcon from '@/assets/images/play/generate.svg';
import uploadIcon from '@/assets/images/play/upload.svg';
import { ReactComponent as MoreIcon } from '@/assets/images/svg/more.svg';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/shadcn/tooltip';
import { ChainId, isBSCNetwork, NETWORK_CONFIG } from '@/constant';
import { Lazbubu } from '@/gql/graph/__generated__/request';
import { useDataAnchoringTokenContract } from '@/hooks/useContract';
import { usePersonalityAvailable } from '@/hooks/usePersonalityAvailable';
import useSend from '@/hooks/useSend';
import { useSetPersonalityPermit } from '@/hooks/useSetPersonalityPermit';
import application from '@/stores/application';
import serverConfig from '@/stores/serverConfig';
import user from '@/stores/user';
import Alert from '@/utils/components/alert';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { Box, Chip, Drawer, IconButton, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

interface LazbubuDrawerProps {
    lazbubu: Lazbubu;
}

const StyledDrawer = styled(Drawer)`
    & .MuiDrawer-paper {
        position: fixed;
        top: 80px;
        right: 0;
        height: 100%;
        z-index: 40;
        transform: none;
        transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);

        ${({ theme }) => theme.mediaWidth.upToSmall`
            top: 60px;
        `}
    }

    & .MuiBackdrop-root {
        display: none;
    }
`;

const Image = styled.div<{ src: string }>`
    width: 100%;
    height: 370px;
    background-image: url(${(props) => props.src});
    background-size: cover;
    background-position: center;
    border-radius: 10px 10px 0 0;
    overflow: hidden;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        height: 300px;
    `}
`;

const StyledIconButton = styled(IconButton)`
    position: absolute;
    top: 0;
    right: 20px;
    z-index: 50;
    height: 40px;
    width: 40px;
    background-color: white;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -2px rgba(0, 0, 0, 0.05);
    border-radius: 8px;
    transition: background-color 0.15s ease-in-out;

    ${({ theme }) => theme.mediaWidth.upToSmall`
    display: none;
    `};

    svg {
        color: #000;
    }

    &:hover {
        background-color: #fff;
    }
`;

export const LazbubuDrawer: React.FC<LazbubuDrawerProps> = observer(
    ({ lazbubu }) => {
        const { address } = user;
        const send = useSend();
        const { personalityAvailable, getPersonalityAvailable } =
            usePersonalityAvailable(lazbubu.tokenId);
        const dataAnchoringTokenContract = useDataAnchoringTokenContract();
        const { getSetPersonalityPermit } = useSetPersonalityPermit(
            lazbubu.tokenId,
        );

        const [generatePersonalityLoading, setGeneratePersonalityLoading] =
            useState(false);

        useEffect(() => {
            getPersonalityAvailable();
            const interval = setInterval(() => {
                getPersonalityAvailable();
            }, 10000);
            return () => clearInterval(interval);
        }, []);

        const handleDrawerClose = () => {
            application.closeLazbubuDrawer();
        };

        const onClickDat = (tokenId: number) => {
            const explorer =
                NETWORK_CONFIG[user.chainId || ChainId.lazaiTestnet].explore;
            if (isBSCNetwork) {
                window.open(
                    `${explorer}/nft/${serverConfig?.config?.contracts.dataAnchoringTokenAddress}/${tokenId}`,
                    '_blank',
                );
            } else {
                window.open(
                    `${explorer}/token/${serverConfig?.config?.contracts.dataAnchoringTokenAddress}/instance/${tokenId}`,
                    '_blank',
                );
            }
        };

        const onClickOwner = () => {
            const explorer =
                NETWORK_CONFIG[user.chainId || ChainId.lazaiTestnet].explore;
            window.open(`${explorer}/address/${address}`, '_blank');
        };

        const handleDrawerOpen = () => {
            application.openLazbubuDrawer();
        };

        const generatePersonality = async () => {
            if (
                personalityAvailable &&
                dataAnchoringTokenContract &&
                !generatePersonalityLoading
            ) {
                setGeneratePersonalityLoading(true);
                const permit = await getSetPersonalityPermit();
                if (!permit?.data || !permit?.permit) {
                    Alert({
                        type: 'error',
                        text: 'get permit failed. Please try again.',
                    });
                    return;
                }
                const permitData = JSON.parse(permit.data);
                const permitValue = JSON.parse(permit.permit);
                send({
                    contract: dataAnchoringTokenContract,
                    method: 'setPersonality',
                    params: [
                        lazbubu.tokenId,
                        permitData.personality,
                        [
                            Number(permitValue.permitType),
                            BigInt(permitValue.nonce),
                            permitValue.dataHash,
                            BigInt(permitValue.expire),
                            permitValue.sig,
                        ],
                    ],
                    onSuccess: () => {
                        getPersonalityAvailable();
                        setGeneratePersonalityLoading(false);
                    },
                    onError: (e) => {
                        setGeneratePersonalityLoading(false);
                        if (e?.code !== 'ACTION_REJECTED') {
                            Alert({
                                type: 'error',
                                text:
                                    e?.shortMessage ||
                                    e?.message ||
                                    'Personality generation failed. Please try again.',
                            });
                        }
                    },
                });
            }
        };

        return (
            <>
                {!application.isLazbubuDrawerOpen && (
                    <StyledIconButton
                        onClick={handleDrawerOpen}
                        aria-label="open drawer"
                    >
                        <MoreIcon />
                    </StyledIconButton>
                )}
                <StyledDrawer
                    anchor="right"
                    open={application.isLazbubuDrawerOpen}
                    onClose={handleDrawerClose}
                    variant="persistent"
                    className="[&_.MuiDrawer-paper]:w-[350px] max-md:[&_.MuiDrawer-paper]:w-[250px] [&_.MuiDrawer-paper]:bg-white [&_.MuiDrawer-paper]:shadow-lg [&_.MuiDrawer-paper]:border-none [&_.MuiDrawer-paper]:absolute [&_.MuiDrawer-paper]:top-[80px] [&_.MuiDrawer-paper]:right-0 [&_.MuiDrawer-paper]:h-full [&_.MuiDrawer-paper]:z-40 [&_.MuiDrawer-paper]:transform-none [&_.MuiDrawer-paper]:transition-transform [&_.MuiDrawer-paper]:duration-300 [&_.MuiDrawer-paper]:ease-in-out [&_.MuiBackdrop-root]:hidden"
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                >
                    {/* Close Button */}
                    <IconButton
                        onClick={handleDrawerClose}
                        className="absolute top-8 left-4 z-50 bg-white/80 hover:bg-white shadow-md"
                        size="small"
                    >
                        <KeyboardDoubleArrowRightIcon className="text-gray-600" />
                    </IconButton>

                    {/* Lazbubu Image */}
                    <Box className="flex justify-center flex-col items-center gap-4 pb-6 bg-gradient-to-b from-cyan-50 to-white">
                        <div className="w-full h-auto relative">
                            <Image
                                src={
                                    lazbubu.image
                                        ? lazbubu.image
                                        : require('@/assets/images/lazbubu-default.png')
                                }
                            />
                            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-transparent to-white"></div>
                        </div>
                        <img
                            className="w-[160px] h-[160px] rounded-[20px] shadow-lg mt-[-100px] z-10 max-md:w-[120px] max-md:h-[120px]"
                            src={
                                lazbubu.image
                                    ? lazbubu.image
                                    : require('@/assets/images/lazbubu-default.png')
                            }
                            alt="Lazbubu"
                        />
                        <Typography
                            component="div"
                            className="font-semibold text-black text-[18px]"
                        >
                            {lazbubu.name}
                        </Typography>
                    </Box>
                    <div className="bg-border h-[1px] mx-[65px] my-[16px] max-md:mx-[30px] max-md:my-[10px]"></div>
                    {/* Lazbubu Details */}
                    <Box className="p-6 flex-1">
                        {/* Basic Information */}
                        <Box className="mb-6">
                            <Box className="space-y-3">
                                <Box className="flex justify-between items-center">
                                    <Typography className="text-[14px] text-gray">
                                        Owner
                                    </Typography>
                                    <Typography
                                        className="text-[14px] font-medium text-blue-600 cursor-pointer hover:underline"
                                        onClick={onClickOwner}
                                    >
                                        {lazbubu.masterName}
                                    </Typography>
                                </Box>
                                <Box className="flex justify-between items-center">
                                    <Typography className="text-[14px] text-gray">
                                        Birthday
                                    </Typography>
                                    <Typography className="text-[14px] font-medium">
                                        {new Date(
                                            lazbubu.birthday * 1000,
                                        ).toLocaleDateString()}
                                    </Typography>
                                </Box>
                                <Box className="flex justify-between items-center">
                                    <Typography className="text-[14px] text-gray">
                                        Token ID
                                    </Typography>
                                    <Typography
                                        onClick={() =>
                                            onClickDat(lazbubu.tokenId)
                                        }
                                        className="text-[14px] font-medium text-blue-600 hover:underline cursor-pointer"
                                    >
                                        {lazbubu.tokenId}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Personality Tags */}
                        <Box className="mb-6 pb-10">
                            <div className="flex items-center justify-between mb-4">
                                <Typography className="text-[16px] font-semibold text-gray-800">
                                    Personality Tags
                                </Typography>
                                {lazbubu.personality && (
                                    <TooltipProvider
                                        delayDuration={
                                            !personalityAvailable ? 0 : 3000
                                        }
                                    >
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <img
                                                    src={uploadIcon}
                                                    alt="uploadIcon"
                                                    className={
                                                        !personalityAvailable ||
                                                        generatePersonalityLoading
                                                            ? 'cursor-not-allowed'
                                                            : 'cursor-pointer'
                                                    }
                                                    onClick={
                                                        !personalityAvailable ||
                                                        generatePersonalityLoading
                                                            ? undefined
                                                            : generatePersonality
                                                    }
                                                />
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p>
                                                    Chat more with Lazbubu to
                                                    extract personality
                                                </p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </div>
                            {lazbubu.personality && (
                                <Box className="flex flex-wrap gap-2">
                                    {lazbubu.personality
                                        .split(',')
                                        .map((personality) => (
                                            <Chip
                                                key={personality}
                                                label={personality}
                                                size="small"
                                                className="bg-blue-50 text-blue-600 border-blue-200"
                                                variant="outlined"
                                            />
                                        ))}
                                </Box>
                            )}

                            {!lazbubu.personality && (
                                <TooltipProvider
                                    delayDuration={
                                        !personalityAvailable ? 0 : 3000
                                    }
                                >
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                className="text-[12px] flex items-center gap-[10px] mt-[20px] shrink-0 max-md:text-[11px] bg-white max-sm:text-[10px] border-border disabled:border-gray disabled:text-gray text-primary border rounded-[12px] px-[20px] max-md:px-[16px] py-[10px] max-md:py-[8px] max-sm:px-[12px] max-sm:py-[6px]"
                                                onClick={generatePersonality}
                                                disabled={
                                                    !personalityAvailable ||
                                                    generatePersonalityLoading
                                                }
                                            >
                                                <img
                                                    src={
                                                        !personalityAvailable ||
                                                        generatePersonalityLoading
                                                            ? generateGrayIcon
                                                            : generateIcon
                                                    }
                                                    className="w-[16px] h-[16px]"
                                                />
                                                Generate Personality
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>
                                                Chat more with Lazbubu to
                                                extract personality
                                            </p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            )}
                        </Box>
                    </Box>
                </StyledDrawer>
            </>
        );
    },
);

export default LazbubuDrawer;
